import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box, Button, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

// Contextes
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useNotifications } from './contexts/NotificationContext';
import { AppProvider, useApp } from './contexts/AppContext';
import { NotificationProvider } from './contexts/NotificationContext';

// Theme
import { getTheme } from './theme';

// Layout
import MainLayout from './components/layout/MainLayout';

// Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import MeetingsListPage from './pages/MeetingsListPage';
import MeetingsListPageExample from './pages/MeetingsListPageExample';
import MeetingDetailsPage from './pages/MeetingDetailsPage';
import LoginPage from './pages/LoginPage';
import AdminPage from './pages/admin/AdminPage';
import SystemLogsPage from './pages/admin/SystemLogsPage';
import ProfilePage from './pages/ProfilePage';
import ModelsListPage from './pages/ModelsListPage';
import NewModelPage from './pages/NewModelPage';
import EditModelPage from './pages/EditModelPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailsPage from './pages/ProjectDetailsPage';

import ChangelogPage from './pages/ChangelogPage';
import AuthNotification from './components/AuthNotification';
import GlobalLoader from './components/GlobalLoader';

// Page temporaire pour les routes non encore implémentées
const UnderConstructionPage = ({ title }) => (
  <Box sx={{ textAlign: 'center', py: 5 }}>
    <CircularProgress sx={{ mb: 2 }} />
    <h2>{title || 'Page en cours de développement'}</h2>
    <p>Cette fonctionnalité sera bientôt disponible.</p>
  </Box>
);

// Composant pour gérer la dépendance circulaire entre AuthProvider et NotificationProvider
const AppProviders = ({ children }) => {
  return (
    <AuthProvider>
      <NotificationProvider>
        <AppProvider>
          {children}
        </AppProvider>
      </NotificationProvider>
    </AuthProvider>
  );
};

const App = () => {
  // On déplace la logique d'authentification dans un composant séparé
  // pour pouvoir utiliser les hooks dans un sous-composant
  return (
    <AppProviders>
      <AppContent />
    </AppProviders>
  );
};

// Composant séparé pour pouvoir utiliser les hooks d'authentification
const AppContent = () => {
  const { isAuthenticated, loading, authStatus } = useAuth();
  const { addSystemNotification } = useNotifications();
  const { theme } = useApp();
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const hideInitialLoader = () => {
      const loader = document.getElementById('initial-loader');
      if (loader) {
        loader.classList.add('hidden');
        setTimeout(() => {
          loader.style.display = 'none';
        }, 300);
      }
    };

    if (!loading && !isLoading) {
      hideInitialLoader();
    }
  }, [loading, isLoading]);

  const prefersDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

  const themeMode = theme === 'system' ? (prefersDarkMode ? 'dark' : 'light') : theme;
  const currentTheme = getTheme(themeMode);

  // Effet pour écouter les changements de préférence de thème système
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        // Force un rafraîchissement du composant
        // pour mettre à jour le thème
        forceUpdate(Date.now());
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);
  
  // Fonction pour forcer un rafraîchissement du composant
  const [, forceUpdate] = useState();
  
  // Fonction pour nettoyer le localStorage (DEBUG)
  const clearLocalStorage = () => {
    localStorage.removeItem('testUser');
    localStorage.removeItem('azureTestUser');
    localStorage.removeItem('redirectInProgress');
    window.location.reload();
  };
  
  useEffect(() => {
    // Vérification de l'authentification
    const checkAuth = async () => {
      try {
        // Attendre que l'authentification soit terminée
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        addSystemNotification('Erreur d', 'error');
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, []);
  
  // Effect pour vérifier la présence d'un utilisateur Azure dans localStorage
  useEffect(() => {
    // Vérifier la présence d'un utilisateur Azure dans localStorage
    const checkAzureUser = () => {
      const azureTestUser = localStorage.getItem('azureTestUser');
      
      // SUPPRESSION DE LA BOUCLE DE RAFRAÎCHISSEMENT
      // Ne plus forcer le rafraîchissement ici
    };
    
    // Exécuter immédiatement
    checkAzureUser();
    
    // Plus besoin d'écouter les changements de chemin
    // window.addEventListener('popstate', checkAzureUser);
    
    // Nettoyer l'écouteur
    return () => {
      // window.removeEventListener('popstate', checkAzureUser);
    };
  }, [isAuthenticated]);
  
  if (isLoading || loading) {
    return null;
  }
  
  // Rediriger vers la page de login si l'utilisateur n'est pas authentifié
  // Mais éviter les redirections pendant que l'authentification est en cours
  if (!isAuthenticated && location.pathname !== '/login' && authStatus !== 'processing') {
    return <Navigate to="/login" replace />;
  }
  
  // Rediriger vers le tableau de bord si l'utilisateur est authentifié et essaie d'accéder à la page de login
  // Mais seulement si l'authentification est complètement terminée
  if (isAuthenticated && location.pathname === '/login' && authStatus !== 'loading' && authStatus !== 'processing') {
    return <Navigate to="/" replace />;
  }
  
  return (
    <ThemeProvider theme={currentTheme}>
      <Routes>
        {/* Route de connexion accessible uniquement si non authentifié */}
        <Route path="/login" element={<LoginPage />} />
        
        {/* Routes protégées - accessibles uniquement si authentifié */}
        <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
        <Route path="/about" element={<MainLayout><AboutPage /></MainLayout>} />
        <Route path="/meetings" element={<MainLayout><MeetingsListPage /></MainLayout>} />
        <Route path="/meetings-example" element={<MainLayout><MeetingsListPageExample /></MainLayout>} />
        <Route path="/meetings/:id" element={<MainLayout><MeetingDetailsPage /></MainLayout>} />
        <Route path="/admin" element={<MainLayout><AdminPage /></MainLayout>} />
        <Route path="/admin/system-logs" element={<MainLayout><SystemLogsPage /></MainLayout>} />
        <Route path="/profile" element={<MainLayout><ProfilePage /></MainLayout>} />
        <Route path="/models" element={<MainLayout><ModelsListPage /></MainLayout>} />
        <Route path="/models/new" element={<MainLayout><NewModelPage /></MainLayout>} />
        <Route path="/models/:id/edit" element={<MainLayout><EditModelPage /></MainLayout>} />
        <Route path="/projects" element={<MainLayout><ProjectsPage /></MainLayout>} />
        <Route path="/projects/:projectId" element={<MainLayout><ProjectDetailsPage /></MainLayout>} />

        <Route path="/changelog" element={<MainLayout><ChangelogPage /></MainLayout>} />
        
        {/* Route 404 */}
        <Route path="*" element={<MainLayout><UnderConstructionPage title="Page non trouvée" /></MainLayout>} />
      </Routes>
      
      {/* Ajouter le composant de notification d'authentification */}
      <AuthNotification />
      <GlobalLoader />
    </ThemeProvider>
  );
};

export default App;
